import { type LoaderFunction } from '@remix-run/node';
import { useLoaderData, Link } from '@remix-run/react';
import { useTranslation } from "react-i18next";
import { Container } from '#app/components/container.tsx';
import { FadeIn } from '#app/components/fade-in.tsx';
import MarkdownRenderer from '#app/components/markdown-renderer.tsx';
import { Button } from "#app/components/ui/button.tsx";
import {Icon} from "#app/components/ui/icon.tsx";
import { type Projects, projects } from "#app/content/projects.ts";
import { getLang, i18n, isSupportedLanguage } from '#app/utils/i18n.ts';
import { analytics } from "#app/utils/segment.ts";

export const loader: LoaderFunction = async ({ params }) => {
	const { slug } = params;
	if (!slug) {
		throw new Response('Not Found', { status: 404 });
	}

	let lang = getLang(params);

	if (!isSupportedLanguage(lang) || params.lng === i18n.fallbackLng) {
		lang = i18n.fallbackLng;
	}

	if (!slug) {
		throw new Response('Not Found', { status: 404 });
	}

	const project = projects[lang][slug];

	if (!project) {
		throw new Response('Not Found', { status: 404 });
	}

	return { project };
};

function Blockquote ({ children }: { children: React.ReactNode }) {
	return (
		<blockquote className="relative mt-3 text-lg font-normal text-primary-darker">
			{children}
		</blockquote>
	);
}

const Project = () => {
	const { t } = useTranslation();
	const { project } = useLoaderData<{ project: Projects }>();

	const components = {
		header: FadeIn,
		a: ({ href, children, ...props }: { href: string; children: React.ReactNode }) => {
			if (href.startsWith('/')) {
				return (
					<Link to={href} {...props}>
						{children}
					</Link>
				);
			}
			return (
				<a href={href} {...props}>
					{children}
				</a>
			);
		},
		blockquote: Blockquote,
		p: ({ children, ...props }: { children: React.ReactNode }) => {
			return (
				<p className="mt-6 max-w-prose text-lg font-normal text-primary-darker" {...props}>
					{children}
				</p>
			);
		},
	};

	//
	return (
		<Container className="max-w-prose text-xl">
			<section
				className={'flex min-h-[calc(100vh-400px)] items-center justify-center'}
			>
				<FadeIn className="max-w-4xl">
					<h1 className="relative mt-16 text-center font-display text-6xl font-bold tracking-tight text-primary [text-wrap:balance] sm:text-7xl">
						{project.title}
					</h1>
					<p className="mt-16 text-center text-lg font-normal text-primary-darker">
						{project.description}
					</p>
					<div className={'mt-6 text-center'}>
						<Link
							onClick={() => {
								analytics.track('Projects Clicked', {
									location: 'Header',
								});
							}}
							className={''}
							to={project.url}
						>
							<Icon name={'chain'} className={'text-primary'} size={'xl'} />
						</Link>
					</div>
				</FadeIn>
			</section>

			<section>
				<img
					className={'rounded-30'}
					src="/img/projects/ley-beckham.png"
					alt={`${project.title} screen grab`}
				/>
				<MarkdownRenderer
					// @ts-ignore
					components={components}
					content={project.content} />
			</section>
			<section
				className={'rounded-30 relative mt-16 bg-primary-darker p-10 md:p-16'}
			>
				<h1 className="relative text-center font-display text-2xl font-medium tracking-tight text-primary-foreground [text-wrap:balance] sm:text-5xl">
					{t('project.call-to-action.title')}
				</h1>
				<div className="mt-12 flex justify-center">
					<Button asChild variant={'secondary'}>
						<Link
							onClick={() => {
								analytics.track('Contact Us Clicked', {
									location: 'Header',
								});
							}}
							to="/contact"
						>
							{t('project.call-to-action.button')}
						</Link>
					</Button>
				</div>
				<svg
					viewBox="0 0 261 260"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					className="absolute bottom-0 right-[10%] z-10 h-[95px] w-[96px] -translate-y-1/2 translate-x-1/2 transform sm:h-[130px] sm:w-[131px]"
				>
					<path
						d="M165.802 184.465L118.997 260L125.343 182.079L34.9058 219.45L99.164 159.817L0 163.792L78.538 134.373L0 74.7401L86.471 89.052V0L118.997 89.052L165.802 10.3364L149.143 97.798L217.368 29.419L165.802 112.905L245.927 81.101L165.802 143.914H261L165.802 184.465Z"
						fill="#4C41DE"
					/>
				</svg>
			</section>
		</Container>
	);
};

export default Project;
